/**
 * German wording for the validation rules the room booking API enforces.
 *
 * The API only reports *which* rule failed: `makeValidationErrorResponse` returns
 * `{ field: { messageKey: text } }`, where the message keys (e.g. `stringLengthTooLong`)
 * are the contract and the text is an untranslated library default. Every German
 * sentence a user gets to see for a rejected booking is produced here, so the same
 * wording is used no matter whether a rule was caught in the form or by the server.
 */

/** Free-text length limits (must match the RoomBookingManagement input filters). */
export const TITLE_MAX_LENGTH = 40;
export const DESCRIPTION_MAX_LENGTH = 250;

/** Shown whenever the server reports a rule we have no specific wording for. */
const FALLBACK_MESSAGE =
	"Die Eingaben sind ungültig. Bitte überprüfen Sie das Formular.";

/**
 * German sentence per field and validator message key.
 *
 * Only rules we have actually verified belong here. Anything else falls back to the
 * generic sentence on purpose: a guessed translation would state something specific and
 * possibly wrong, which is worse than admitting the input was invalid.
 */
const VALIDATION_MESSAGES = {
	title: {
		stringLengthTooLong: `Der Titel darf maximal ${TITLE_MAX_LENGTH} Zeichen lang sein`,
		stringLengthTooShort: "Der Titel darf nicht leer sein",
	},
	description: {
		stringLengthTooLong: `Die Beschreibung darf maximal ${DESCRIPTION_MAX_LENGTH} Zeichen lang sein`,
	},
	// DateTimeImmutableValidator, configured with greaterThanField "startTime" and a
	// 30-minute minimum offset. Its own message templates are English, so these two are
	// the rules users actually run into.
	endTime: {
		minimumNotMet: "Die Buchung muss mindestens 30 Minuten lang sein",
		notGreaterThan: "Die Endzeit muss nach der Startzeit liegen",
	},
};

/**
 * German wording for a single validation rule.
 * @param {string} field - Field name as reported by the server
 * @param {string} messageKey - Validator message key, e.g. "stringLengthTooLong"
 * @returns {string} German sentence, or a generic one for unmapped rules
 */
export function getFieldValidationMessage(field, messageKey) {
	return VALIDATION_MESSAGES[field]?.[messageKey] ?? FALLBACK_MESSAGE;
}

/**
 * Turn a server validation payload into German sentences.
 * @param {Object} messages - `messages` from the API: { field: { messageKey: text } }
 * @returns {string[]} One sentence per reported rule, duplicates removed
 */
export function translateValidationMessages(messages) {
	if (!messages || typeof messages !== "object") {
		return [];
	}

	const sentences = [];

	for (const [field, fieldMessages] of Object.entries(messages)) {
		if (!fieldMessages || typeof fieldMessages !== "object") {
			continue;
		}

		for (const messageKey of Object.keys(fieldMessages)) {
			// Deliberately ignore the server's own text - it is never user-facing
			const sentence = getFieldValidationMessage(field, messageKey);
			if (!sentences.includes(sentence)) {
				sentences.push(sentence);
			}
		}
	}

	return sentences;
}

/**
 * Single German message describing why the server rejected the input.
 * @param {Object} messages - `messages` from the API: { field: { messageKey: text } }
 * @returns {string} Never empty, never a raw message key
 */
export function getValidationErrorMessage(messages) {
	const sentences = translateValidationMessages(messages);

	return sentences.length > 0 ? sentences.join(" ") : FALLBACK_MESSAGE;
}
